import React, { useState, useEffect, useMemo } from 'react';

// Clave de evento que debe ser consistente con los otros componentes (Pacientes, Citas, etc.)
const DATA_CHANGE_EVENT = 'data-changed'; 

// =========================================================================
// INLINE SVG ICONS (Para garantizar el componente compile sin dependencias)
// =========================================================================

const IconWrapper = ({ children, className, ...props }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className} 
        {...props}
    >
        {children}
    </svg>
);

const UserCheckIcon = (props) => ( // Médicos
    <IconWrapper {...props}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></IconWrapper>
);

const UsersIcon = (props) => ( // Pacientes
    <IconWrapper {...props}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><path d="M20 8v6"></path><path d="M23 11H17"></path></IconWrapper>
);

const CalendarCheckIcon = (props) => ( // Citas
    <IconWrapper {...props}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line><path d="M10 16l2 2l4-4"></path></IconWrapper>
);

const MicroscopeIcon = (props) => ( // Laboratorios
    <IconWrapper {...props}><path d="M6 18H2"></path><path d="M10 12L3 5"></path><path d="M21 21L10 10"></path><path d="M14.5 5.5l1 1"></path><path d="M13 10L14 11"></path><path d="M16 6L10 12"></path><path d="M21 3L15 9"></path><path d="M6 18v3"></path></IconWrapper>
);

const FileTextIcon = (props) => ( // Recetas
    <IconWrapper {...props}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line></IconWrapper>
);

const ActivityIcon = (props) => ( // Desempeño
    <IconWrapper {...props}><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></IconWrapper>
);

const BriefcaseIcon = (props) => ( // Ocupado
    <IconWrapper {...props}><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></IconWrapper>
);

// Claves de localStorage
const STORAGE_KEYS = {
    medicos: 'adminMedicosData',
    pacientes: 'adminPacientesData',
    citas: 'adminCitasData',
    recetas: 'adminRecetasData',
    laboratorios: 'adminLaboratoriosData',
};


// =========================================================================
// DATOS INVENTADOS ACTUALIZADOS (SOLO NOMBRES)
// =========================================================================

/**
 * Función para simular la carga de datos.
 * @param {string} key - La clave de almacenamiento.
 * @returns {Array<Object>} Un array de objetos simulados.
 */
const mockLoadData = (key) => {
    // Definimos las fechas para simular citas pasadas y futuras
    const today = new Date();
    const pastDate1 = new Date(today.getTime() - (86400000 * 5)).toISOString().split('T')[0];
    const pastDate2 = new Date(today.getTime() - (86400000 * 10)).toISOString().split('T')[0];
    const futureDate1 = new Date(today.getTime() + (86400000 * 3)).toISOString().split('T')[0];
    const futureDate2 = new Date(today.getTime() + (86400000 * 7)).toISOString().split('T')[0];

    // Nombres actualizados (SOLO NOMBRES)
    const DR_JAVIER = 'Javier';
    const DRA_DIANA = 'Diana';
    const DRA_LISSET = 'Lisset';

    switch (key) {
        // --- 1. Médicos (4 items) - NOMBRES ACTUALIZADOS ---
        case 'adminMedicosData':
            return [
                { id: 'M001', nombre: DR_JAVIER, especialidad: 'Cardiología', activo: true },
                { id: 'M002', nombre: DRA_DIANA, especialidad: 'Pediatría', activo: true },
                { id: 'M003', nombre: DRA_LISSET, especialidad: 'Dermatología', activo: true },
                { id: 'M004', nombre: 'Hugo', especialidad: 'General', activo: false },
            ];

        // --- 2. Pacientes (10 items) ---
        case 'adminPacientesData':
            return [
                { id: 'P001', nombre: 'Ana García', edad: 35 },
                { id: 'P002', nombre: 'Juan López', edad: 52 },
                { id: 'P003', nombre: 'María Rodríguez', edad: 28 },
                { id: 'P004', nombre: 'Pedro Sánchez', edad: 67 },
                { id: 'P005', nombre: 'Elena Fernández', edad: 19 },
                { id: 'P006', nombre: 'Carlos Ruiz', edad: 44 },
                { id: 'P007', nombre: 'Marta Jiménez', edad: 31 },
                { id: 'P008', nombre: 'David Castro', edad: 22 },
                { id: 'P009', nombre: 'Lucía Morales', edad: 58 },
                { id: 'P010', nombre: 'Andrés Gil', edad: 70 },
            ];

        // --- 3. Citas (15 items) - MÉDICOS ACTUALIZADOS ---
        case 'adminCitasData':
            return [
                // Javier (10 citas: 6 cumplidas, 4 pendientes) - Médico Más Ocupado
                { id: 1, fecha: pastDate1, hora: '10:00', paciente: 'Ana García', medico: DR_JAVIER },
                { id: 2, fecha: pastDate1, hora: '11:00', paciente: 'Juan López', medico: DR_JAVIER },
                { id: 3, fecha: pastDate2, hora: '09:00', paciente: 'María Rodríguez', medico: DR_JAVIER },
                { id: 4, fecha: pastDate2, hora: '14:00', paciente: 'Pedro Sánchez', medico: DR_JAVIER },
                { id: 5, fecha: pastDate1, hora: '16:00', paciente: 'Elena Fernández', medico: DR_JAVIER },
                { id: 6, fecha: pastDate2, hora: '17:00', paciente: 'Carlos Ruiz', medico: DR_JAVIER },
                { id: 7, fecha: futureDate1, hora: '10:00', paciente: 'Marta Jiménez', medico: DR_JAVIER },
                { id: 8, fecha: futureDate1, hora: '12:00', paciente: 'David Castro', medico: DR_JAVIER },
                { id: 9, fecha: futureDate2, hora: '15:00', paciente: 'Lucía Morales', medico: DR_JAVIER },
                { id: 10, fecha: futureDate2, hora: '16:00', paciente: 'Andrés Gil', medico: DR_JAVIER },
                
                // Diana (3 citas: 2 cumplidas, 1 pendiente)
                { id: 11, fecha: pastDate1, hora: '10:30', paciente: 'Elena Fernández', medico: DRA_DIANA },
                { id: 12, fecha: pastDate2, hora: '11:30', paciente: 'Ana García', medico: DRA_DIANA },
                { id: 13, fecha: futureDate1, hora: '13:00', paciente: 'Pedro Sánchez', medico: DRA_DIANA },
                
                // Lisset (2 citas: 1 cumplida, 1 pendiente)
                { id: 14, fecha: pastDate1, hora: '15:00', paciente: 'María Rodríguez', medico: DRA_LISSET },
                { id: 15, fecha: futureDate2, hora: '10:00', paciente: 'Juan López', medico: DRA_LISSET },
            ];

        // --- 4. Recetas (4 items - MÉDICOS ACTUALIZADOS) ---
        case 'adminRecetasData':
            return [
                { id: 'R004', paciente: 'Lucía Morales', medico: DR_JAVIER, fecha_emision: '14-Dic-2025', medicamento: 'Atorvastatina' },
                { id: 'R003', paciente: 'David Castro', medico: DR_JAVIER, fecha_emision: '10-Dic-2025', medicamento: 'Ibuprofeno' },
                { id: 'R002', paciente: 'Elena Fernández', medico: DRA_DIANA, fecha_emision: '05-Dic-2025', medicamento: 'Amoxicilina' },
                { id: 'R001', paciente: 'Ana García', medico: DR_JAVIER, fecha_emision: '01-Dic-2025', medicamento: 'Paracetamol' },
            ];

        // --- 5. Laboratorios (5 items - MÉDICOS ACTUALIZADOS) ---
        case 'adminLaboratoriosData':
            return [
                { id: 'L005', paciente: 'Juan López', examen: 'Perfil Lipídico', medico: DR_JAVIER, resultado: 'Anormal', fecha_resultado: '14-Dic-2025' },
                { id: 'L004', paciente: 'Pedro Sánchez', examen: 'Glucosa en Ayunas', medico: DR_JAVIER, resultado: 'Normal', fecha_resultado: '12-Dic-2025' },
                { id: 'L003', paciente: 'María Rodríguez', examen: 'Biometría Hemática', medico: DRA_LISSET, resultado: 'Normal', fecha_resultado: '10-Dic-2025' },
                { id: 'L002', paciente: 'Ana García', examen: 'Examen General de Orina', medico: DRA_DIANA, resultado: 'Anormal', fecha_resultado: '08-Dic-2025' },
                { id: 'L001', paciente: 'Carlos Ruiz', examen: 'Tiempos de Coagulación', medico: DR_JAVIER, resultado: 'Normal', fecha_resultado: '05-Dic-2025' },
            ];

        default:
            return [];
    }
};

// =========================================================================
// FUNCIÓN DE CARGA DE DATOS (MODIFICADA PARA USAR MOCK DATA)
// =========================================================================

// Función para obtener los datos de localStorage de forma segura (AHORA USA mockLoadData)
const loadData = (key) => {
    // Si quisieras volver a usar localStorage, descomenta el bloque try/catch original.
    // Por ahora, usamos los datos inventados para que el componente funcione.
    const data = mockLoadData(key);
    console.log(`[DEBUG: MOCK LOAD] Key: ${key} - Items cargados: ${data.length}`);
    return data;
};

/**
 * Componente principal de Reportes.
 */
export default function App() {
    // Estado para almacenar todos los datos consolidados
    const [data, setData] = useState({
        medicos: [],
        pacientes: [],
        citas: [],
        recetas: [],
        laboratorios: [],
    });

    // Función que carga todos los datos y actualiza el estado
    const reloadData = () => {
        const loadedData = {
            medicos: loadData(STORAGE_KEYS.medicos),
            pacientes: loadData(STORAGE_KEYS.pacientes),
            citas: loadData(STORAGE_KEYS.citas),
            recetas: loadData(STORAGE_KEYS.recetas),
            laboratorios: loadData(STORAGE_KEYS.laboratorios),
        };
        setData(loadedData);
        
        const totalItems = Object.values(loadedData).reduce((sum, arr) => sum + arr.length, 0);
        console.log(`[DATA] Total de items recargados: ${totalItems}. El panel es reactivo.`);
    };

    // Cargar datos al iniciar y configurar el listener de reactividad
    useEffect(() => {
        // 1. Cargar datos al montar por primera vez
        reloadData(); 
        
        // 2. Definir el listener para recargar datos cuando otro componente guarde (REACTIVIDAD)
        window.addEventListener(DATA_CHANGE_EVENT, reloadData);

        // 3. Cleanup: remover el listener al desmontar el componente
        return () => {
            window.removeEventListener(DATA_CHANGE_EVENT, reloadData);
        };
    }, []); // Dependencia vacía: se ejecuta solo en mount y unmount.

    // --- Lógica de Cálculo de Métricas (Memoizada) ---
    const metrics = useMemo(() => {
        // 1. Calcular número de citas por médico (para el gráfico)
        const citasPorMedicoCounts = data.citas.reduce((acc, cita) => {
            const medicoName = cita.medico || 'Sin Asignar'; 
            acc[medicoName] = (acc[medicoName] || 0) + 1;
            return acc;
        }, {});

        const citasPorMedico = Object.keys(citasPorMedicoCounts).map(medico => ({
            name: medico,
            value: citasPorMedicoCounts[medico],
        })).sort((a, b) => b.value - a.value); // Ordenar de mayor a menor

        // 2. Calcular estadísticas de Citas
        const totalCitas = data.citas.length;
        // La comparación de fechas funciona porque en mockLoadData se usa ISOString (YYYY-MM-DD)
        const citasPendientes = data.citas.filter(c => new Date(c.fecha) >= new Date(new Date().toDateString())).length;
        const citasCumplidas = totalCitas - citasPendientes; 
        
        // 3. Obtener el médico con más citas (para destacar)
        const medicoMasOcupado = citasPorMedico[0] || { name: 'N/A', value: 0 };
        
        // 4. Calcular el porcentaje de uso de laboratorios por paciente (simulación)
        const pacientesConLab = new Set(data.laboratorios.map(lab => lab.paciente));
        const porcentajeUsoLab = data.pacientes.length > 0 
            ? Math.round((pacientesConLab.size / data.pacientes.length) * 100) 
            : 0;

        return {
            totalMedicos: data.medicos.length,
            totalPacientes: data.pacientes.length,
            totalCitas,
            totalLaboratorios: data.laboratorios.length,
            citasCumplidas,
            citasPendientes,
            medicoMasOcupado,
            citasPorMedico,
            porcentajeUsoLab
        };
    }, [data]);

    // --- Componente de Tarjeta de Métrica (Reutilizable) ---
    const MetricCard = ({ title, value, color, icon: Icon, description }) => (
        <div className="bg-white rounded-xl shadow-lg transition-all duration-300 transform hover:scale-[1.02]">
            <div className={`p-6 border-b-4 rounded-t-xl ${color.border} ${color.bg} flex items-center justify-between`}>
                <div className='flex items-center space-x-4'>
                    <div className={`p-3 rounded-full ${color.iconBg}`}>
                        <Icon className={`w-6 h-6 ${color.icon}`} />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-600 uppercase">{title}</p>
                        <p className="text-3xl font-extrabold text-gray-900 mt-1">{value}</p>
                    </div>
                </div>
            </div>
            {description && (
                <div className='p-3 text-sm text-gray-500'>
                    {description}
                </div>
            )}
        </div>
    );

    // --- Componente Gráfico Sencillo (Barras) ---
    const BarChart = ({ data, title }) => {
        const maxValue = Math.max(...data.map(d => d.value), 1); 

        return (
            <div className="bg-white p-6 rounded-xl shadow-2xl h-full flex flex-col">
                <h3 className="text-xl font-bold text-gray-800 mb-6 border-b pb-2">{title}</h3>
                {data.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-gray-500 italic">No hay citas registradas para graficar.</div>
                ) : (
                    <div className="space-y-4 flex-1 overflow-y-auto">
                        {data.map((item, index) => {
                            const widthPercent = (item.value / maxValue) * 100;
                            
                            // Color basado en el ranking
                            let barColor = 'bg-indigo-500';
                            if (index === 0) barColor = 'bg-teal-500';
                            else if (index === 1) barColor = 'bg-indigo-500';
                            else if (index === 2) barColor = 'bg-purple-500';

                            return (
                                <div key={index} className="flex items-center">
                                    <span className="w-28 text-sm font-semibold text-gray-700 truncate">{item.name}:</span>
                                    <div className="flex-1 ml-4 bg-gray-200 rounded-lg h-7 relative">
                                        <div 
                                            className={`h-full ${barColor} rounded-lg flex items-center justify-end pr-3 text-white font-bold text-sm transition-all duration-500`} 
                                            style={{ width: `${widthPercent}%` }}
                                        >
                                            {item.value}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // --- Componente Lista de Actividad Reciente ---
    const RecentActivityList = ({ title, icon: Icon, items, emptyMessage, renderItem }) => (
        <div className="bg-white p-6 rounded-xl shadow-2xl h-full flex flex-col">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                <Icon className='w-5 h-5 mr-2 text-indigo-600' /> {title}
            </h3>
            <ul className="divide-y divide-gray-100 flex-1 overflow-y-auto">
                {items.length === 0 ? (
                    <div className="py-8 text-center text-gray-500 italic">{emptyMessage}</div>
                ) : (
                    items.slice(0, 7).map(renderItem)
                )}
            </ul>
        </div>
    );


    // --- Estilos de color predefinidos ---
    const colorStyles = {
        medicos: { border: 'border-blue-500', bg: 'bg-blue-50', icon: 'text-blue-600', iconBg: 'bg-blue-200' },
        pacientes: { border: 'border-green-500', bg: 'bg-green-50', icon: 'text-green-600', iconBg: 'bg-green-200' },
        citas: { border: 'border-indigo-500', bg: 'bg-indigo-50', icon: 'text-indigo-600', iconBg: 'bg-indigo-200' },
        laboratorios: { border: 'border-red-500', bg: 'bg-red-50', icon: 'text-red-600', iconBg: 'bg-red-200' },
    };


    return (
        <div className="p-4 sm:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen font-sans">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2">
                <span className='text-indigo-600'>📊</span> Reportes Clínicos
            </h1>
            <p className='text-gray-500 mb-8'>Vista consolidada de métricas clave y actividad reciente de la clínica.</p>
            
            
            {/* Sección de Métricas Clave */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <MetricCard 
                    title="Total Médicos" 
                    value={metrics.totalMedicos} 
                    color={colorStyles.medicos}
                    icon={UserCheckIcon}
                />
                <MetricCard 
                    title="Total Pacientes" 
                    value={metrics.totalPacientes} 
                    color={colorStyles.pacientes}
                    icon={UsersIcon}
                />
                <MetricCard 
                    title="Citas Programadas" 
                    value={metrics.totalCitas} 
                    color={colorStyles.citas}
                    icon={CalendarCheckIcon}
                />
                <MetricCard 
                    title="Pruebas Laboratorio" 
                    value={metrics.totalLaboratorios} 
                    color={colorStyles.laboratorios}
                    icon={MicroscopeIcon}
                />
            </div>
            
            {/* Sección de Desempeño y Citas */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                
                {/* 1. Gráfico de Citas por Médico */}
                <div className="lg:col-span-2">
                    <BarChart 
                        title="Distribución de Citas por Médico (Top 7)"
                        data={metrics.citasPorMedico.slice(0, 7)}
                    />
                </div>

                {/* 2. Resumen de Citas y Desempeño */}
                <div className="bg-white p-6 rounded-xl shadow-2xl border-t-4 border-indigo-600 flex flex-col justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <ActivityIcon className='w-5 h-5 mr-2 text-indigo-600' /> Resumen de Citas
                        </h3>
                        <div className="space-y-4">
                            <div className='flex justify-between p-3 bg-green-50 rounded-lg'>
                                <span className="text-gray-700 font-semibold">Citas Cumplidas (Pasadas):</span>
                                <span className="text-xl font-extrabold text-green-600">{metrics.citasCumplidas}</span>
                            </div>
                            <div className='flex justify-between p-3 bg-yellow-50 rounded-lg'>
                                <span className="text-gray-700 font-semibold">Citas Pendientes (Futuras):</span>
                                <span className="text-xl font-extrabold text-yellow-600">{metrics.citasPendientes}</span>
                            </div>
                            <div className="pt-4 border-t mt-4 border-gray-200">
                                <p className="text-md font-bold text-gray-800 flex items-center">
                                    <BriefcaseIcon className='w-4 h-4 mr-1' /> Médico Más Ocupado:
                                </p>
                                <p className="text-2xl font-extrabold text-indigo-600 mt-1">
                                    {metrics.medicoMasOcupado.name}
                                </p>
                                <p className='text-sm text-gray-500'>
                                    con {metrics.medicoMasOcupado.value} citas.
                                </p>
                            </div>
                            <div className="pt-4 border-t mt-4 border-gray-200">
                                <p className="text-md font-bold text-gray-800 flex items-center">
                                    <MicroscopeIcon className='w-4 h-4 mr-1' /> Uso de Laboratorio:
                                </p>
                                <p className="text-2xl font-extrabold text-red-600 mt-1">
                                    {metrics.porcentajeUsoLab}%
                                </p>
                                <p className='text-sm text-gray-500'>
                                    Pacientes con resultados de laboratorio.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sección de Listas de Actividad Reciente */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Lista de Recetas */}
                <RecentActivityList
                    title="Últimas Recetas Emitidas"
                    icon={FileTextIcon}
                    items={data.recetas}
                    emptyMessage="No hay recetas registradas."
                    renderItem={(receta) => (
                        <li key={receta.id} className="py-3 flex justify-between items-center">
                            <div>
                                <p className="text-sm font-bold text-gray-900 truncate">
                                    Receta para {receta.paciente}
                                </p>
                                <p className="text-xs text-gray-600">
                                    Médico: {receta.medico || 'N/A'}
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-indigo-500 bg-indigo-100 px-2 py-1 rounded-full whitespace-nowrap">
                                {receta.fecha_emision}
                            </span>
                        </li>
                    )}
                />

                {/* Lista de Laboratorios */}
                <RecentActivityList
                    title="Últimos Resultados de Laboratorio"
                    icon={MicroscopeIcon}
                    items={data.laboratorios}
                    emptyMessage="No hay resultados de laboratorio registrados."
                    renderItem={(lab) => (
                        <li key={lab.id} className="py-3 flex justify-between items-center">
                            <div>
                                <p className="text-sm font-bold text-gray-900 truncate">
                                    {lab.examen}
                                </p>
                                <p className="text-xs text-gray-600">
                                    Paciente: {lab.paciente || 'N/A'}
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-red-500 bg-red-100 px-2 py-1 rounded-full whitespace-nowrap">
                                {lab.fecha_resultado}
                            </span>
                        </li>
                    )}
                />
            </div>
            
        </div>
    );
}